/* eslint-disable react/prop-types */
import React, { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import useAppDispatch from 'hooks/useAppDispatch';
import { useVoip } from 'hooks/useVoip';
import QuickbooksAppIcon from 'housecall-ui/dist/IconsAndImages/AppIcons/QuickbooksAppIcon';
import CallIcon from 'housecall-ui/dist/IconsAndImages/Icons/Call';
import Map from 'housecall-ui/dist/IconsAndImages/Icons/Map';
import NavMyApps from 'housecall-ui/dist/IconsAndImages/Icons/NavMyApps';
import Settings from 'housecall-ui/dist/IconsAndImages/Icons/Settings';
import PropTypes from 'prop-types';
import { fetchQboErrorsCount, getQboErrorsCount } from 'resources/QuickbooksOnline/Errors/Data';
import { AmplitudeFlagKeys } from 'types/amplitudeFlags';

import useIsExperimentActive from 'components/Amplitude/useIsExperimentActive';
import { fetchData } from 'components/Common/Data/DataFetcher';
import RedirectTo from 'components/Common/RedirectTo';
import { getSettingsPath } from 'components/Settings/SettingsV2/utils';

import { openProPhone } from './Utils';

const NavbarShortcutStates = ({ quickbooksBadgeData, showCompanySetup, onMyAppsClick }) => {
  const isSettingsWithLeftRailActive = useIsExperimentActive(AmplitudeFlagKeys.settingsWithLeftRail);
  const dispatch = useAppDispatch();
  const [loaded, canUseVoip] = useVoip();
  const qboErrorCount = useSelector(getQboErrorsCount);

  useEffect(() => {
    dispatch(fetchData(fetchQboErrorsCount()));
  }, [dispatch]);

  const { qboEnabled, canUseQbo, qbdEnabled, canUseQbd, qbdErrorCount } = quickbooksBadgeData;

  const qboErrorsLink = useCallback(() => {
    if (qboEnabled) {
      return RedirectTo.qboSettingsSyncAlerts();
    }

    return RedirectTo.qbdErrors();
  }, [qboEnabled]);

  const qbErrorDisplayCount = useCallback(() => {
    if (qboEnabled) {
      return qboErrorCount ? '' : qboErrorCount;
    }

    if (qbdEnabled) return qbdErrorCount;

    return 0;
  }, [qboEnabled, qbdEnabled, qboErrorCount, qbdErrorCount]);

  const showQbo = canUseQbo && qboEnabled;
  const showQbd = canUseQbd && qbdEnabled;
  return [
    {
      icon: <CallIcon />,
      onClick: () => {
        openProPhone();
      },
      title: 'Dialer',
      show: loaded && canUseVoip,
      value: 'dialer',
    },
    {
      badgeCount: qbErrorDisplayCount(),
      badgeVariant: qboEnabled ? 'dot' : 'standard',
      icon: <QuickbooksAppIcon />,
      onClick: () => qboErrorsLink(),
      title: 'QuickBooks',
      show: showQbo || showQbd,
      value: 'quickbooks',
    },
    {
      icon: <Map />,
      onClick: () => RedirectTo.map(),
      title: 'Map',
      value: 'map',
      show: true,
    },
    {
      icon: <NavMyApps />,
      onClick: (event) => {
        onMyAppsClick(event);
        event.preventDefault();
      },
      title: 'My apps',
      show: true,
      value: 'apps',
    },
    {
      icon: <Settings />,
      onClick: () =>
        isSettingsWithLeftRailActive ? RedirectTo.appSettings(getSettingsPath()) : RedirectTo.accountSettings(),
      title: 'Settings',
      show: showCompanySetup,
      value: 'settings',
    },
  ];
};
NavbarShortcutStates.propTypes = {
  showCompanySetup: PropTypes.bool,
  quickbooksBadgeData: PropTypes.shape({
    qboErrorCount: PropTypes.number,
    qbdErrorCount: PropTypes.number,
    qboEnabled: PropTypes.bool,
    qbdEnabled: PropTypes.bool,
  }),
  onMyAppsClick: PropTypes.func,
};

export default NavbarShortcutStates;
